"use client";

import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { AI_AGENT_ROLE, resolveAiName } from "@/lib/ai-assistant";
import { htmlToPreviewText } from "@/lib/html-to-preview-text";
import { ANCHOR_REFS } from "@/lib/launcher-anchors";
import type { UnreadPeekMessage } from "@/lib/use-visitor-unread";
import { useWidgetConfig } from "@/lib/use-widget-config";
import { useWidgetEnv } from "@/lib/use-widget-env";
import { cn } from "@/lib/utils";
import { CrossFade } from "./cross-fade";
import { buttonKeys } from "./quick-replies/button-keys";
import {
  CHIP_HIT_AREA,
  CHIP_TOUCH_GAP_CARD,
  chipLabelClampOnSide,
} from "./quick-replies/chip-classes";
import { useQuickReplyGroupProps } from "./quick-replies/use-quick-reply-group-props";
import { useIframePeekCap } from "./use-iframe-peek-cap";

interface LauncherPeekProps {
  message: UnreadPeekMessage | null;
  /** Visitor tapped the body of the peek → open the panel directly
   *  on the Messages tab. */
  onOpen: () => void;
  /** Visitor dismissed the peek (X chip). Doesn't mark read — just
   *  hides until the next inbound. */
  onDismiss: () => void;
  /** Visitor tapped a quick-reply button. The shell sends the
   *  payload as a visitor message, then opens the panel so the
   *  visitor sees the conversation. */
  onSendButton: (payload: string, text: string) => void;
  /** Which corner the launcher + panel anchor to — the peek pins to
   *  the SAME side. Defaults to `"right"`. */
  position?: "left" | "right";
  /** Anchor to the host `<div>` (`absolute`) instead of the viewport
   *  (`fixed`). Set by the React-component embed — see `WidgetEnv.embedded`. */
  embedded?: boolean;
}

/**
 * Floating preview pinned above the closed launcher FAB — surfaces
 * the latest unread operator message so visitors can read & quick-
 * reply without opening the panel.
 *
 * **Composition** is two stacked surfaces (per Day-12 design pass):
 *
 *   1. **Text bubble** — message body, dismiss X. Tapping the body
 *      opens the panel on Messages. No avatar, no sender label —
 *      visitors recognise messages from the FAB context.
 *
 *   2. **Quick-reply chips** — each chip is its own pill, stacked
 *      vertically below the text bubble (NOT inside it). Tap →
 *      send the payload as the visitor's reply + open the panel.
 *
 * **Layout**: anchored bottom-right above the launcher, max-width
 * 300. Stacked block lets the buttons read as discrete tap targets
 * rather than a chip row crammed into the bubble.
 */
export function LauncherPeek({
  message,
  onOpen,
  onDismiss,
  onSendButton,
  position = "right",
  embedded = false,
}: LauncherPeekProps) {
  const t = useTranslations("peek");
  const quickReplyGroup = useQuickReplyGroupProps();
  const buttonKeysOf = buttonKeys(message?.buttons ?? []);
  const onLeft = position === "left";
  // iframe: the viewport IS the frame, which is measured from the peek, so a dvh cap would make
  // it ramp up (see the hook). Everywhere else the cap is the real page viewport.
  const inIframe = useWidgetEnv().kind === "iframe";
  const iframeCap = useIframePeekCap(inIframe, message?.id);
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          // A slot message keeps one key: a re-pick cross-fades the content in place (bottom-pinned, no jump).
          key={message.inPlaceKey ?? message.id}
          // Measurement anchor: the iframe shell reads this element's box (see
          // WidgetShell's closed-frame resize effect) to grow the launcher
          // frame so the whole peek bubble fits. Harmless on other embeds.
          data-wx-peek=""
          ref={ANCHOR_REFS.peek}
          // Bouncy pop-in from lower + smaller so the peek "springs up" from
          // the launcher rather than just fading; low damping = a little
          // overshoot.
          initial={{ opacity: 0, y: 18, scale: 0.88 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 440, damping: 20 }}
          // Esc dismisses a slot message while focus is inside it.
          onKeyDown={
            message.inPlaceKey
              ? (e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    onDismiss();
                  }
                }
              : undefined
          }
          className={cn(
            // One below the launcher so an overlapping tap still lands
            // on the launcher; both well above any host-page chrome.
            "bottom-24 z-2147483599 flex flex-col gap-3",
            "w-[300px]",
            embedded ? "absolute" : "fixed",
            onLeft ? "left-4" : "right-4",
          )}
          // Host offset (px) from the corner — mirrors the launcher FAB so the
          // peek stays stacked above it. `--wx-off-*` default 0 (no change).
          style={{
            marginBottom: "var(--wx-off-y,0px)",
            ...(onLeft
              ? { marginLeft: "var(--wx-off-x,0px)" }
              : { marginRight: "var(--wx-off-x,0px)" }),
            // Never taller than the space above the launcher (same 7rem reserve
            // as the panel), so the sender header + dismiss X stay on screen with
            // many quick replies, a tall thumbnail or a short viewport. The
            // bubble body and the chip list scroll inside what is left.
            maxHeight: inIframe
              ? (iframeCap ?? undefined)
              : embedded
                ? "calc(100% - 7rem - var(--wx-off-y,0px))"
                : "calc(100dvh - 7rem - var(--wx-off-y,0px))",
          }}
          role="dialog"
          aria-label={t("newMessage")}
        >
          <CrossFade
            id={message.id}
            enabled={!!message.inPlaceKey}
            anchor="end"
            className="relative min-h-0"
            innerClassName="flex min-h-0 flex-col gap-3"
          >
            {/* Text bubble — message body + dismiss. Tap the body to
              open the panel; X dismisses without marking read.
              TipTap operator content arrives as HTML; the peek isn't
              the right surface for block rendering (it sits inside a
              `<button>`), so we collapse to a plain-text preview. */}
            {(message.text || message.thumbUrl) && (
              // Lifts on hover to signal it's tappable (opens the panel).
              <motion.div
                // A clickable surface: the iframe embed keeps it in the frame's hit area.
                data-wx-hit=""
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 400, damping: 24 }}
                className={cn(
                  // `overflow-hidden` clips a media thumbnail's bottom corners to
                  // the bubble radius so the image can sit flush to the edges.
                  // The bubble gives up space last (almost-zero shrink): on a short viewport the quick
                  // replies scroll and the message stays readable; only once they are fully collapsed
                  // does a very long message scroll inside the bubble.
                  "relative flex min-h-0 shrink-[0.01] flex-col overflow-hidden rounded-wx-lg border border-wx-border bg-wx-bg text-wx-fg",
                  // Tighter drop (less downward bleed) so the soft shadow doesn't
                  // wash over the quick-reply chips stacked just below.
                  "shadow-[0_10px_28px_rgba(0,0,0,0.14)]",
                )}
              >
                {/* Header row: sender (opens the panel) + dismiss X, vertically
                  aligned as flex siblings so the X lines up with the name. */}
                <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-3 pb-2">
                  <button
                    type="button"
                    onClick={onOpen}
                    className="min-w-0 flex-1 text-start transition-opacity hover:opacity-90"
                  >
                    <PeekSender sender={message.sender} />
                  </button>
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label={t("dismiss")}
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                      "text-wx-fg-muted transition-colors hover:bg-wx-bg-elevated hover:text-wx-fg",
                      // Visually 24px, but a 44px touch target (the pseudo-element extends the hit area).
                      "relative before:absolute before:-inset-2.5 before:content-['']",
                    )}
                  >
                    <X size={12} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onOpen}
                  className={cn(
                    "block min-h-0 w-full overflow-y-auto text-start",
                    "transition-opacity hover:opacity-90",
                  )}
                >
                  {message.thumbUrl && (
                    // biome-ignore lint/performance/noImgElement: operator media thumbnail of arbitrary host/dimensions; next/image needs known sizes + an allowlist
                    <img
                      src={message.thumbUrl}
                      {...(message.thumbW && message.thumbH
                        ? { width: message.thumbW, height: message.thumbH }
                        : {})}
                      alt=""
                      // Flush to the bubble edges (the bubble clips the rounded
                      // bottom corners) at the media's NATURAL aspect (no crop). BE
                      // dims (`thumbW`/`thumbH`) reserve the exact aspect box BEFORE
                      // load so there's no 0→full-height jump; without them it falls
                      // back to natural sizing on load.
                      style={
                        message.thumbW && message.thumbH
                          ? { aspectRatio: `${message.thumbW} / ${message.thumbH}` }
                          : undefined
                      }
                      className="block h-auto w-full bg-wx-bg-elevated object-contain"
                    />
                  )}
                  {message.text && (
                    // The padding lives on this wrapper, NOT on the clamped element: a line-clamped
                    // box clips at its padding edge, so bottom padding on it showed the next line cut
                    // in half under the ellipsis.
                    <span
                      className={cn(
                        "block px-4 pb-4 text-sm leading-relaxed",
                        // A caption under a flush image needs its own top padding
                        // (the image has none); a text-only peek's top spacing
                        // comes from the header's `pb-2`.
                        message.thumbUrl ? "pt-2" : undefined,
                      )}
                    >
                      <span
                        dir="auto"
                        className={cn(
                          "block wrap-anywhere",
                          // Preserve the structured preview's line breaks + bullets
                          // (htmlToPreviewText emits "\n" for blocks / "- " for list
                          // items) instead of collapsing to one run-on line.
                          "whitespace-pre-line",
                          // Cap very long operator messages in the peek — it's a
                          // preview, not the full thread. Truncates with an ellipsis
                          // after 10 lines; tapping opens the panel to read it all.
                          "line-clamp-10",
                        )}
                      >
                        {htmlToPreviewText(message.text)}
                      </span>
                    </span>
                  )}
                </button>
              </motion.div>
            )}

            {/* Quick-reply chips — discrete cards stacked below the
              text bubble. Each is its own tap target with the same
              brand-styled pill we use under chat bubbles. */}
            {message.buttons.length > 0 && (
              // `relative z-10` keeps the chips above the text bubble's stacking
              // context (its hover transform creates one), so the bubble's shadow
              // never sits over a chip and hover/tap targets stay crisp.
              <div
                {...quickReplyGroup}
                // Physical layout: the stack sits on the launcher side whatever the text direction
                // (an RTL UI must not flip it); each label carries its own direction (`dir="auto"`).
                dir="ltr"
                className={cn(
                  // Scrolls when the chips outgrow the space (and gives up space twice as fast
                  // as the message bubble, so the text stays readable); the negative margin
                  // + padding keep each chip's shadow inside the clip box.
                  // The padding (cancelled by the negative margin) must cover the chip shadow
                  // (0 4px 12px: ~8 up, 16 down, 12 sideways) or it is cut in a hard edge.
                  "relative z-10 -mx-3 -mt-2 -mb-4 flex min-h-0 shrink-2 flex-col gap-1.5 overflow-y-auto px-3 pt-2 pb-4",
                  CHIP_TOUCH_GAP_CARD,
                  onLeft ? "items-start" : "items-end",
                )}
              >
                {message.buttons.map((btn, i) => (
                  <motion.button
                    key={buttonKeysOf[i]}
                    data-wx-hit=""
                    type="button"
                    onClick={() => onSendButton(btn.payload, btn.text)}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 480, damping: 26 }}
                    className={cn(
                      // Idle: clean `wx-bg` surface (matches the
                      // outgoing-side message bubbles) lifted by a
                      // soft shadow so it reads as a tap target.
                      // Hover: fills with full brand → text inverts
                      // to the primary foreground.
                      "max-w-full rounded-full bg-wx-bg px-3.5 py-2 text-sm font-medium text-wx-primary",
                      CHIP_HIT_AREA,
                      "transition-colors hover:bg-wx-primary hover:text-wx-primary-fg",
                      "shadow-[0_4px_12px_rgba(0,0,0,0.08)]",
                    )}
                  >
                    <span dir="auto" className={chipLabelClampOnSide(onLeft)}>
                      {btn.text}
                    </span>
                  </motion.button>
                ))}
              </div>
            )}
          </CrossFade>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Sender header rendered above the peek preview text — avatar +
 * display name + role suffix (when no name is configured). Mirrors
 * the bubble attribution inside the conversation so visitors see who
 * pinged them before they tap to open. Falls back to a neutral
 * monogram if the operator hasn't uploaded an avatar.
 */
function PeekSender({ sender }: { sender: UnreadPeekMessage["sender"] }) {
  const { config } = useWidgetConfig();
  const t = useTranslations("peek");
  const isGaia = sender.kind === "ai" || sender.kind === "bot";
  // Name ONLY — no avatar/monogram in the peek header. AI / bot reads as the
  // operator-configured agent name (or "AI Agent" when the agent identity is
  // disabled); an operator's own name flows through, falling back to the generic
  // role label when we have no name.
  const displayName = isGaia
    ? (resolveAiName(config.messenger?.aiAssistant) ?? AI_AGENT_ROLE)
    : (sender.name?.trim() ?? "");
  const roleLabel = displayName ? null : sender.kind === "operator" ? t("operator") : null;
  return <span className="text-xs font-medium text-wx-fg-muted">{displayName || roleLabel}</span>;
}
